// This file is auto-generated by @hey-api/openapi-ts

import type { Config } from './types.gen';

export type ServerSentEventsOptions<TData = unknown> = Omit<RequestInit, 'method'> &
  Pick<Config, 'method' | 'responseTransformer' | 'responseValidator'> & {
    /**
     * Fetch API implementation. You can use this option to provide a custom
     * fetch instance.
     *
     * @default globalThis.fetch
     */
    fetch?: typeof fetch;
    /**
     * Implementing clients can call request interceptors inside this hook.
     */
    onRequest?: (url: string, init: RequestInit) => Promise<Request>;
    /**
     * Callback invoked when a network or parsing error occurs during streaming.
     *
     * This option applies only if the endpoint returns a stream of events.
     *
     * @param error The error that occurred.
     */
    onSseError?: (error: unknown) => void;
    /**
     * Callback invoked when an event is streamed from the server.
     *
     * This option applies only if the endpoint returns a stream of events.
     *
     * @param event Event streamed from the server.
     * @returns Nothing (void).
     */
    onSseEvent?: (event: StreamEvent<TData>) => void;
    serializedBody?: RequestInit['body'];
    /**
     * Default retry delay in milliseconds.
     *
     * This option applies only if the endpoint returns a stream of events.
     *
     * @default 3000
     */
    sseDefaultRetryDelay?: number;
    /**
     * Maximum number of retry attempts before giving up.
     */
    sseMaxRetryAttempts?: number;
    /**
     * Maximum retry delay in milliseconds.
     *
     * Applies only when exponential backoff is used.
     *
     * This option applies only if the endpoint returns a stream of events.
     *
     * @default 30000
     */
    sseMaxRetryDelay?: number;
    /**
     * Optional sleep function for retry backoff.
     *
     * Defaults to using `setTimeout`.
     */
    sseSleepFn?: (ms: number) => Promise<void>;
    url: string;
  };

export interface StreamEvent<TData = unknown> {
  data: TData;
  event?: string;
  id?: string;
  retry?: number;
}

export type ServerSentEventsResult<TData = unknown, TReturn = void, TNext = unknown> = {
  stream: AsyncGenerator<
    TData extends Record<string, unknown> ? TData[keyof TData] : TData,
    TReturn,
    TNext
  >;
};

type SseState = Readonly<{ lastEventId?: string; retryDelay: number }>;

type SseFrameState = Readonly<{
  dataLines: string[];
  eventName?: string;
  lastEventId?: string;
  retryDelay: number;
}>;

type ParsedSseChunk<TData> = Readonly<{
  event: StreamEvent<TData>;
  hasData: boolean;
  isJson: boolean;
  state: SseState;
}>;

function parseSseField(state: SseFrameState, line: string): SseFrameState {
  if (line.startsWith('data:')) return { ...state, dataLines: [...state.dataLines, line.replace(/^data:\s*/, '')] };
  if (line.startsWith('event:')) return { ...state, eventName: line.replace(/^event:\s*/, '') };
  if (line.startsWith('id:')) return { ...state, lastEventId: line.replace(/^id:\s*/, '') };
  if (!line.startsWith('retry:')) return state;
  const parsed = Number.parseInt(line.replace(/^retry:\s*/, ''), 10);
  return Number.isNaN(parsed) ? state : { ...state, retryDelay: parsed };
}

function parseSseData(dataLines: string[]): Readonly<{ data: unknown; isJson: boolean }> {
  if (!dataLines.length) return { data: undefined, isJson: false };
  const rawData = dataLines.join('\n');
  try { return { data: JSON.parse(rawData), isJson: true }; }
  catch { return { data: rawData, isJson: false }; }
}

function parseSseChunk<TData>(chunk: string, previous: SseState): ParsedSseChunk<TData> {
  const initial: SseFrameState = { dataLines: [], retryDelay: previous.retryDelay, ...(previous.lastEventId !== undefined ? { lastEventId: previous.lastEventId } : {}) };
  const parsed = chunk.split('\n').reduce<SseFrameState>(
    (state, line) => parseSseField(state, line),
    initial,
  );
  const { data, isJson } = parseSseData(parsed.dataLines);
  const state = { retryDelay: parsed.retryDelay, ...(parsed.lastEventId !== undefined ? { lastEventId: parsed.lastEventId } : {}) };
  const event = { data: data as TData, retry: state.retryDelay, ...(parsed.eventName !== undefined ? { event: parsed.eventName } : {}), ...(state.lastEventId !== undefined ? { id: state.lastEventId } : {}) };
  return { event, hasData: parsed.dataLines.length > 0, isJson, state };
}

async function prepareSseChunk<TData>(parsed: ParsedSseChunk<TData>, validator?: (data: unknown) => Promise<unknown>, transformer?: (data: unknown) => Promise<unknown>): Promise<ParsedSseChunk<TData>> {
  if (!parsed.isJson) return parsed;
  if (validator) await validator(parsed.event.data);
  const data = transformer ? await transformer(parsed.event.data) as TData : parsed.event.data;
  return { ...parsed, event: { ...parsed.event, data } };
}

async function* readSseResponse<TData>(response: Response, signal: AbortSignal, initialState: SseState, onState: (state: SseState) => void, onEvent?: (event: StreamEvent<TData>) => void, validator?: (data: unknown) => Promise<unknown>, transformer?: (data: unknown) => Promise<unknown>): AsyncGenerator<TData> {
  if (!response.body) throw new Error('No body in SSE response');
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  const abortHandler = () => { reader.cancel().catch(() => {}); };
  signal.addEventListener('abort', abortHandler);
  let buffer = '';
  let state = initialState;
  try {
    while (true) {
      const read = await reader.read();
      if (read.done) break;
      buffer = (buffer + read.value).replace(/\r\n?/g, '\n');
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';
      for (const chunk of chunks) {
        const parsed = await prepareSseChunk(parseSseChunk<TData>(chunk, state), validator, transformer);
        state = parsed.state;
        onState(state);
        onEvent?.(parsed.event);
        if (parsed.hasData) yield parsed.event.data;
      }
    }
  } finally {
    signal.removeEventListener('abort', abortHandler);
    reader.releaseLock();
  }
}

async function fetchSseResponse(url: string, options: ServerSentEventsOptions, signal: AbortSignal, state: SseState, onRequest: ServerSentEventsOptions['onRequest']): Promise<Response> {
  const headers = new Headers(options.headers as HeadersInit | undefined);
  if (state.lastEventId !== undefined) headers.set('Last-Event-ID', state.lastEventId);
  const init: RequestInit = { redirect: 'follow', ...options, headers, signal, ...(options.serializedBody !== undefined ? { body: options.serializedBody } : {}) };
  const request = onRequest ? await onRequest(url, init) : new Request(url, init);
  const response = await (options.fetch ?? globalThis.fetch)(request);
  if (!response.ok) throw new Error(`SSE failed: ${response.status} ${response.statusText}`);
  return response;
}

export function createSseClient<TData = unknown>({ onRequest, onSseError, onSseEvent, responseTransformer, responseValidator, sseDefaultRetryDelay, sseMaxRetryAttempts, sseMaxRetryDelay, sseSleepFn, url, ...options }: ServerSentEventsOptions): ServerSentEventsResult<TData> {
  const sleep = sseSleepFn ?? ((ms: number) => new Promise((resolve) => setTimeout(resolve, ms)));
  const createStream = async function* () {
    let state: SseState = { retryDelay: sseDefaultRetryDelay ?? 3000 };
    let attempt = 0;
    const signal = options.signal ?? new AbortController().signal;
    while (!signal.aborted) {
      attempt += 1;
      try {
        const response = await fetchSseResponse(url, { ...options, url }, signal, state, onRequest);
        const updateState = (next: SseState) => { state = next; };
        yield* readSseResponse<TData>(response, signal, state, updateState, onSseEvent, responseValidator, responseTransformer);
        return;
      } catch (error) {
        onSseError?.(error);
        if (sseMaxRetryAttempts !== undefined && attempt >= sseMaxRetryAttempts) break;
        await sleep(Math.min(state.retryDelay * 2 ** (attempt - 1), sseMaxRetryDelay ?? 30000));
      }
    }
  };
  return { stream: createStream() as ServerSentEventsResult<TData>['stream'] };
}
